import { Clock3, LogOut, ShieldAlert } from "lucide-react";
import { useEffect, useRef } from "react";
import type { AdminUser, AuthConfig } from "../api/client";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(options: { client_id: string; callback: (response: { credential: string }) => void }): void;
          renderButton(element: HTMLElement, options: Record<string, string | number>): void;
          disableAutoSelect(): void;
        };
      };
    };
  }
}

type LoginPageProps = {
  config: AuthConfig;
  loading: boolean;
  error: string;
  onCredential: (credential: string) => void;
};

export function LoginPage({ config, loading, error, onCredential }: LoginPageProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    if (!config.configured || !config.googleClientId) return;
    let cancelled = false;

    function renderGoogleButton() {
      if (cancelled || !buttonRef.current || !window.google) return;
      buttonRef.current.replaceChildren();
      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: ({ credential }) => callbackRef.current(credential)
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        logo_alignment: "left",
        locale: "zh_TW",
        width: 300
      });
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity="true"]');
    if (window.google) {
      renderGoogleButton();
    } else if (existing) {
      existing.addEventListener("load", renderGoogleButton, { once: true });
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client?hl=zh_TW";
      script.async = true;
      script.dataset.googleIdentity = "true";
      script.addEventListener("load", renderGoogleButton, { once: true });
      document.head.append(script);
    }
    return () => {
      cancelled = true;
    };
  }, [config.configured, config.googleClientId]);

  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="login-title">
        <div className="auth-brand">
          <span className="auth-brand-mark">HK</span>
          <div>
            <strong>HKET Analytics</strong>
            <span>Article views</span>
          </div>
        </div>
        <div className="auth-copy">
          <span className="auth-eyebrow">管理後台</span>
          <h1 id="login-title">登入文章觀看統計</h1>
          <p>請使用 Google 帳號登入。首次登入後，需由管理員啟用帳號才能查看分析資料。</p>
        </div>
        {config.configured ? (
          <div className={`google-login-slot${loading ? " is-loading" : ""}`} ref={buttonRef} aria-busy={loading} />
        ) : (
          <div className="auth-config-warning">
            <ShieldAlert size={18} aria-hidden="true" />
            <span>尚未設定 Google Web Client ID，請聯絡系統管理員完成登入設定。</span>
          </div>
        )}
        {error ? <div className="auth-error">{error}</div> : null}
        <span className="auth-security-note">系統不接受密碼或其它登入方式</span>
      </section>
    </main>
  );
}

export function AccountStatusPage({ user, onLogout }: { user: AdminUser; onLogout: () => void }) {
  const disabled = user.status === "disabled";
  return (
    <main className="auth-shell">
      <section className="auth-panel account-status-panel" aria-labelledby="account-status-title">
        <div className={`account-status-icon${disabled ? " disabled" : ""}`}>
          {disabled ? <ShieldAlert size={28} aria-hidden="true" /> : <Clock3 size={28} aria-hidden="true" />}
        </div>
        <div className="auth-copy">
          <span className="auth-eyebrow">{user.email}</span>
          <h1 id="account-status-title">{disabled ? "帳號已停用" : "帳號等待管理員啟用"}</h1>
          <p>
            {disabled
              ? "此帳號目前不能存取後台資源，請聯絡管理員重新啟用。"
              : "管理員啟用後，頁面會自動開放總覽、文章、作者及其它分析區塊。"}
          </p>
        </div>
        <button className="secondary-action auth-logout" type="button" onClick={onLogout}>
          <LogOut size={16} aria-hidden="true" />
          <span>登出</span>
        </button>
      </section>
    </main>
  );
}
